import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import CustomSelect from "./CustomSelect";
import { useTemplate } from "@utils/useTemplate";
import { templateTypeMapping } from "../utils/constants";

interface TemplateFormProps {
  isEditing?: boolean;
  children?: React.ReactNode;
}

const TemplateForm: React.FC<TemplateFormProps> = ({ isEditing, children }) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const nameMaxCharacters = 50;
  const descriptionMaxCharacters = 200;

  const { fetchTemplatTypes } = useTemplate();
  const [templateTypeOptions, setTemplateTypeOptions] = useState<
    { label: string; value: string }[]
  >([]);

  useEffect(() => {
    (async () => {
      try {
        const types: string[] = await fetchTemplatTypes();
        let formattedOptions = types
          .filter((type) => templateTypeMapping[type])
          .map((type) => ({
            label: templateTypeMapping[type],
            value: templateTypeMapping[type],
          }));
        formattedOptions = [
          { label: "Select Report Type", value: "" },
          ...formattedOptions,
        ];
        setTemplateTypeOptions(formattedOptions);
      } catch (error) {
        console.error("Failed to fetch template types", error);
      }
    })();
  }, []);

  return (
    <div className="h-[calc(100%-30px)] flex flex-col pb-0 pr-4">
      <div className="flex flex-col flex-1 min-h-0">
        {/* Template Name */}
        <div className="py-4">
          <label
            htmlFor="templateName"
            className="block text-gray-700 mb-2 text-sm 2xl:text-base"
          >
            Template Name<span className="text-red-600">*</span>
          </label>
          <input
            data-testid="template-name"
            id="templateName"
            {...register("templateName", {
              required: "Template name is required",
              maxLength: {
                value: nameMaxCharacters,
                message: `Max ${nameMaxCharacters} characters`,
              },
            })}
            className={`w-full h-[3rem] border text-sm 2xl:text-base border-gray-300 rounded-md p-3 focus:outline-none focus:border-blue-500 ${
              !isEditing ? "text-slate-400 bg-gray-100 cursor-not-allowed" : ""
            }`}
            placeholder="Add a name"
            disabled={!isEditing}
          />
          {errors.templateName && (
            <p className="text-red-500 text-sm">
              {String(errors.templateName.message)}
            </p>
          )}
        </div>

        {/* Description */}
        <div className="pb-4">
          <label
            htmlFor="templateDescription"
            className="block text-gray-700 mb-2 text-sm 2xl:text-base"
          >
            Template Description <span className="text-red-600">*</span>
          </label>
          <textarea
            data-testid="template-desc"
            id="templateDescription"
            {...register("templateDescription", {
              required: "Template Description is required",
              maxLength: {
                value: descriptionMaxCharacters,
                message: `Max ${descriptionMaxCharacters} characters`,
              },
            })}
            className={`w-full h-[3rem] text-sm 2xl:text-base border overflow-y-hidden border-gray-300 rounded-md p-3 focus:outline-none focus:border-blue-500 ${
              !isEditing ? "text-slate-400 bg-gray-100 cursor-not-allowed" : ""
            }`}
            placeholder="Template's purpose and who is it for..."
            rows={1}
            disabled={!isEditing}
          />
          {errors.templateDescription && (
            <p className="text-red-500 text-sm">
              {String(errors.templateDescription.message)}
            </p>
          )}
        </div>

        {/* Report Type Dropdown */}
        <div className="pb-4">
          <label
            htmlFor="templateType"
            className="block text-gray-700 mb-2 text-sm 2xl:text-base"
          >
            Report Type
          </label>
          <div id="templateType">
            <CustomSelect
              name="templateType"
              options={templateTypeOptions}
              placeholder="Select report type"
              disabled={!isEditing}
              useFormContext={useFormContext()}
              showFirstValueSeparator={true}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1 mb-1">
            Selecting a report type provides the system with more context, thus
            improving the output
          </p>
        </div>

        {/* Report Outline */}
        <div className="flex flex-1 flex-col min-h-0">
          <label
            htmlFor="templatePrompt"
            className="block text-gray-700 mb-2 text-sm 2xl:text-base"
          >
            Provide a structure for the report{" "}
            <span className="text-red-600">*</span>
          </label>
          <textarea
            data-testid="template-structure"
            id="templatePrompt"
            {...register("templatePrompt", {
              required: "Template Outline is required",
            })}
            className={`w-full flex-1 text-sm 2xl:text-base border border-gray-300 rounded-md p-3 focus:outline-none focus:border-blue-500 resize-none ${
              !isEditing ? "text-slate-400 bg-gray-100 cursor-not-allowed" : ""
            }`}
            placeholder={`Title\nSection A\nSection B`}
            disabled={!isEditing}
          />
          {errors.templatePrompt && (
            <p className="text-red-500 text-sm">
              {String(errors.templatePrompt.message)}
            </p>
          )}
        </div>
      </div>

      {/* Children */}
      {children}
    </div>
  );
};

export default TemplateForm;
